import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getFSAReconciliation,
  registerFSAReconciliation,
  getFSAReconciliationHistory,
} from '@/services/ops.service';
import type {
  FSADrillDownSelection,
  FSAReconciliationData,
  FSARegisterRequest,
  FSAReconciliationMonth,
} from '@/types/fsa-reconciliation.types';
import type { ReconciliationRecord } from '@/types/collector-reconciliation.types';
import { FSA_MOCK_DATA } from '@/features/reconciliation/mock/fsa-mock-data';

const CACHE_KEY = 'fsa-reconciliation';
const HISTORY_KEY = 'fsa-reconciliation-history';

const USE_MOCK = false;

// ─── Backend → Frontend adapter ─────────────────────────────────────────────
// Cuando el endpoint real esté listo, este adapter normaliza el shape.

function adaptResponse(raw: Record<string, unknown>): FSAReconciliationData {
  const resumen = raw.resumen as Record<string, unknown>;
  const totals = resumen.totals as Record<string, unknown>;
  const mesesRaw = raw.meses as Record<string, Record<string, unknown>> | undefined;

  const meses: Record<string, FSAReconciliationMonth> = {};
  if (mesesRaw) {
    for (const [k, v] of Object.entries(mesesRaw)) {
      const det = (v.detalles ?? {}) as Record<string, Record<string, unknown>[]>;
      meses[k] = {
        ok: (v.ok as number) ?? 0,
        salioSinEntrar: (v.salioSinEntrar as number) ?? 0,
        entroSinSalir: (v.entroSinSalir as number) ?? 0,
        sinBanco: (v.sinBanco as number) ?? 0,
        sinCuadrar: (v.sinCuadrar as number) ?? 0,
        detalles: {
          ok: det.ok ?? [],
          salioSinEntrar: det.salioSinEntrar ?? [],
          entroSinSalir: det.entroSinSalir ?? [],
          sinBanco: det.sinBanco ?? [],
          sinCuadrar: det.sinCuadrar ?? [],
        },
      };
    }
  }

  return {
    resumen: {
      year: resumen.year as number,
      direction: (resumen.direction as 'FSA_A_FMX' | 'FMX_A_FSA') ?? 'FSA_A_FMX',
      totalTransferencias: (resumen.totalTransferencias as number) ?? 0,
      totalMovimientosBanco: (resumen.totalMovimientosBanco as number) ?? 0,
      ...(resumen.timings ? { timings: resumen.timings as Record<string, number> } : {}),
      totals: {
        ok: (totals.ok as number) ?? 0,
        salioSinEntrar: (totals.salioSinEntrar as number) ?? 0,
        entroSinSalir: (totals.entroSinSalir as number) ?? 0,
        sinBanco: (totals.sinBanco as number) ?? 0,
        sinCuadrar: (totals.sinCuadrar as number) ?? 0,
      },
    },
    meses,
  };
}

export function useFSAReconciliation() {
  const qc = useQueryClient();
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState(currentYear);
  const [drillDown, setDrillDown] = useState<FSADrillDownSelection | null>(null);

  // ─── Queries ────────────────────────────────────────────────────────────────

  const reconciliationQuery = useQuery({
    queryKey: [CACHE_KEY, year],
    queryFn: async () => {
      if (USE_MOCK) {
        await new Promise((r) => setTimeout(r, 600));
        return { data: { ...FSA_MOCK_DATA, resumen: { ...FSA_MOCK_DATA.resumen, year } } };
      }
      return getFSAReconciliation(year);
    },
    staleTime: 5 * 60 * 1000,
  });

  const historyQuery = useQuery({
    queryKey: [HISTORY_KEY, year],
    queryFn: async () => {
      if (USE_MOCK) return { data: [] as ReconciliationRecord[] };
      return getFSAReconciliationHistory(year);
    },
    staleTime: 5 * 60 * 1000,
  });

  // ─── Mutations ──────────────────────────────────────────────────────────────

  const registerMutation = useMutation({
    mutationFn: (req: FSARegisterRequest) => registerFSAReconciliation(req),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [HISTORY_KEY, year] });
    },
  });

  // ─── Derived data ──────────────────────────────────────────────────────────

  const data: FSAReconciliationData | null = useMemo(() => {
    const raw = reconciliationQuery.data?.data;
    if (!raw) return null;
    if (USE_MOCK) return raw as FSAReconciliationData;
    return adaptResponse(raw as unknown as Record<string, unknown>);
  }, [reconciliationQuery.data]);

  const monthData = useMemo(() => {
    return data?.meses ?? null;
  }, [data]);

  const drillDownItems = useMemo(() => {
    if (!drillDown || !monthData) return [];
    const month = monthData[String(drillDown.month)];
    if (!month?.detalles) return [];
    return (month.detalles as Record<string, unknown[]>)[drillDown.statusKey] ?? [];
  }, [drillDown, monthData]);

  // ─── Actions ────────────────────────────────────────────────────────────────

  const runReconciliation = useCallback(() => {
    reconciliationQuery.refetch();
  }, [reconciliationQuery]);

  const handleCellClick = useCallback((month: number, statusKey: string) => {
    setDrillDown({ month, statusKey });
  }, []);

  const closeDrillDown = useCallback(() => {
    setDrillDown(null);
  }, []);

  return {
    // State
    year,
    setYear,
    drillDown,

    // Data
    data,
    resumen: data?.resumen ?? null,
    monthData,
    drillDownItems,
    history: (historyQuery.data?.data ?? []) as ReconciliationRecord[],

    // Status
    isLoading: reconciliationQuery.isFetching,
    isError: reconciliationQuery.isError,
    hasData: data !== null,
    isRegistering: registerMutation.isPending,

    // Actions
    runReconciliation,
    handleCellClick,
    closeDrillDown,
    register: registerMutation.mutate,
    refetch: reconciliationQuery.refetch,
  };
}
